<template>
  <v-dialog :value="value" :width="1400" persistent eager>
    <v-card style="position: relative">
      <AdCardTitle dialog>
          {{ $t('rendered-questionnaire') }}
      </AdCardTitle>

      <v-card-text class="pa-4">
        <PanelLoading :loading="true" v-if="loadingQuestionnaire"/>
        <template v-if="!fhirVersion">
          <AdInfoOwl warning class="ma-0">
            {{ $t('questionnaire-transform-warning')}}
            {{ $t('go-to') }}
            <AdButton link :to="{ name: 'project-overview'}">{{ $t('project-overview')}}.</AdButton>
          </AdInfoOwl>
        </template>
        <div id="lhcFormContainer" :key="containerKey"/>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <div class="d-flex text--disabled" style="gap: 1rem">
          <span v-if="fhirVersion">FHIR {{ $t('version') }}: {{ fhirVersion }}</span>
          <span v-if="lformsVersion">LHC-Forms {{ $t('version') }}: {{ lformsVersion }}</span>
          <span v-else>LHC-Forms</span>
        </div>
        <v-spacer></v-spacer>
        <AdButton type="close" @click="closeModal" />
        <AdButton v-if="userIsEditor && fhirVersion && questionnaireData" type="save" @click="saveResponse" :loading="savingResponse"/>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import PanelLoading from '../Utilities/PanelLoading.vue'
import '@/assets/lforms/styles.css'
import store from '@/store'

const renderLForm = ({ questionnaire, questionnaireResponse }) => {
  const container = document.getElementById('lhcFormContainer')
  const convertedQuestionnaire = window.LForms.Util.convertFHIRQuestionnaireToLForms(questionnaire, 'R4')
  window.LForms.Util.setFHIRContext()

  let formdefinition = convertedQuestionnaire
  if (questionnaireResponse) {
    formdefinition = window.LForms.Util.mergeFHIRDataIntoLForms('QuestionnaireResponses', questionnaireResponse, convertedQuestionnaire, 'R4')
  }
  window.LForms.Util.addFormToPage(formdefinition, container)
    .catch(() => {})
}

export default {
  components: { PanelLoading },
  name: 'QuestionnaireRender',
  props: {
    // Open/close state of the modal
    value: {
      type: Boolean,
      required: false,
      default: false
    },
    questionnaire: {
      type: Object,
      default: null
    }

  },
  data () {
    return {
      containerKey: null,
      loadingQuestionnaire: false,
      questionnaireData: null,
      questionnaireResponseData: null,
      responseData: null,
      savedResponse: null,
      savingResponse: false
    }
  },
  computed: {
    fhirURL () {
      return this.$store.getters['server/serverSettings']?.['url-fhir-services']?.[0]
    },
    fhirVersion () {
      return this.projectInFocus?.restURI
        ?.filter(uri => uri.for === 'FHIR')
        .map(uri => uri.format)
        .sort(this.$filters.numericStringComparator)
        .reverse()[0] || null
    },
    lformsVersion () {
      return (this.containerKey && window.LForms?.lformsVersion) || null
    }
  },
  methods: {
    closeModal () {
      this.$emit('input', false)
    },

    async download (questionnaire) {
      if (!this.fhirVersion) return
      const dataFormat = 'json'

      const project = this.projectInFocus?.prefix
      const { id, effectiveDate } = questionnaire || {}
      if (!(project && id && effectiveDate)) {
        return
      }
      const isQuestionnaireResponse = this.$filters.isQuestionnaireResponse(questionnaire)

      // For QR's, the effectiveDate exists only in the wrapper. It cannot be used to fetch the QR. See AD30-1251.
      const url = isQuestionnaireResponse
        ? `${this.fhirURL.replace(/\/$/, '')}/${this.fhirVersion}/${project}/QuestionnaireResponse/${id}?_format=${dataFormat}`
        : `${this.fhirURL.replace(/\/$/, '')}/${this.fhirVersion}/${project}/Questionnaire/${id}--${effectiveDate.replace(/\D/g, '')}?_format=${dataFormat}`

      this.loadingQuestionnaire = true
      try {
        const res = await axios.get(url)
        this[isQuestionnaireResponse ? 'questionnaireResponseData' : 'questionnaireData'] = res.data || null
      } catch (error) {
        let issueText
        const resData = error?.response?.data
        if (dataFormat === 'json') {
          issueText = resData?.issue?.[0]?.details?.text
        }
        const errorMessage = issueText || resData?.description || error?.response?.statusText || error.message || 'Error'
        this.$oops(errorMessage)
        this.closeModal()
      }
      this.loadingQuestionnaire = false
    },

    async saveResponse () {
      this.savingResponse = true
      const isQuestionnaireResponse = this.$filters.isQuestionnaireResponse(this.questionnaire)
      const lformsFhirVersion = (this.fhirVersion === '3.0') ? 'STU3' : 'R4'
      this.responseData = window.LForms.Util.getFormFHIRData('QuestionnaireResponse', lformsFhirVersion, document.getElementById('lhcFormContainer'))
      const quest = { questionnaire: this.questionnaireData.url }
      Object.assign(this.responseData, quest)
      const url = isQuestionnaireResponse
        ? `${this.fhirURL.replace(/\/$/, '')}/${this.fhirVersion}/${this.projectInFocus?.prefix}/QuestionnaireResponse/${this.questionnaireResponseData?.id}`
        : `${this.fhirURL.replace(/\/$/, '')}/${this.fhirVersion}/${this.projectInFocus?.prefix}/QuestionnaireResponse`
      if (isQuestionnaireResponse) {
        try {
          const res = await axios.put(url, this.responseData, {
            headers: {
              'X-Auth-Token': store.state.authentication.token,
              'content-type': 'application/fhir+json'
            }
          })
          this.savedResponse = res.data || null
        } catch (error) {
          const resData = error?.response?.data
          const issueText = resData?.issue?.[0]?.details?.text
          const errorMessage = issueText || resData?.description || error?.response?.statusText || error.message || 'Error'
          this.$oops(errorMessage)
          this.closeModal()
        }
      } else {
        try {
          const res = await axios.post(url, this.responseData, {
            headers: {
              'X-Auth-Token': store.state.authentication.token,
              'content-type': 'application/fhir+json'
            }
          })
          this.savedResponse = res.data || null
        } catch (error) {
          const resData = error?.response?.data
          const issueText = resData?.issue?.[0]?.details?.text
          const errorMessage = issueText || resData?.description || error?.response?.statusText || error.message || 'Error'
          this.$oops(errorMessage)
          this.closeModal()
        }
      }
      await this.$store.dispatch('questionnaire/getQuestionnaireList')
        .catch(error => {
          const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
          this.$oops(errorMessage)
        })
      this.savingResponse = false
      this.closeModal()
    }
  },
  watch: {
    value: {
      immediate: true,
      async handler (modalIsOpen) {
        if (modalIsOpen) {
          this.containerKey = this.getUuid()
          const isQuestionnaireResponse = this.$filters.isQuestionnaireResponse(this.questionnaire)
          const downloads = [this.download(this.questionnaire)]

          if (isQuestionnaireResponse) {
            const questionnaire = this.$store.getters['questionnaire/questionnaires']?.find(q => q.canonicalUri === this.questionnaire.questionnaire)
            if (questionnaire) {
              downloads.push(this.download(questionnaire))
            }
          }

          await Promise.all(downloads)

          if (this.questionnaireData !== null && (!isQuestionnaireResponse || this.questionnaireResponseData !== null)) {
            renderLForm({ questionnaire: this.questionnaireData, questionnaireResponse: this.questionnaireResponseData })
          }
        } else {
          // Reset when modal closes
          this.questionnaireData = null
          this.questionnaireResponseData = null
          this.loadingQuestionnaire = false
        }
      }
    }

  },
  mounted () {
    const publicPath = process.env.BASE_URL
    const scriptSources = [
      `${publicPath}lforms/36.3.2/zone.min.js`,
      `${publicPath}lforms/36.3.2/runtime.js`,
      `${publicPath}lforms/36.3.2/polyfills.js`,
      `${publicPath}lforms/36.3.2/main.js`,
      `${publicPath}lforms/36.3.2/lformsFHIRAll.min.js`
    ]
    const existingScriptSources = Array.from(document.getElementsByTagName('head')[0].querySelectorAll('script')).map(script => script.src)
    scriptSources.forEach(src => {
      if (!existingScriptSources.some(exSrc => exSrc.endsWith(src))) {
        const el = document.createElement('script')
        el.src = src
        el.async = false
        document.getElementsByTagName('head')[0].appendChild(el)
      }
    })
  }
}
</script>
